/**
 * Translations for the customer-facing /welcome routes (initial register).
 * Kept separate from the passkey `translations.ts` so each surface owns its
 * own typed key set. All four update Fitness locales are required.
 */
import { SUPPORTED_LOCALES, type SupportedLocale } from './translations';

export type WelcomeKeys = {
  request_heading: string;
  request_description: string;
  firstname_label: string;
  lastname_label: string;
  birthdate_label: string;
  checkin_label: string;
  request_submit: string;
  request_sent_heading: string;
  request_sent_message: string;
  error_required_fields: string;
  error_unexpected: string;
  set_heading: string;
  set_description: string;
  reset_heading: string;
  reset_description: string;
  reset_success_heading: string;
  reset_success_message: string;
  username_label: string;
  password_label: string;
  password_repeat_label: string;
  set_submit: string;
  submitting: string;
  verifying: string;
  set_success_heading: string;
  set_success_message: string;
  link_invalid_heading: string;
  link_invalid_message: string;
  request_new_link: string;
  open_in_app: string;
  password_hint: string;
  error_password_mismatch: string;
  error_password_too_short: string;
  error_password_missing_uppercase: string;
  error_password_missing_lowercase: string;
  error_password_missing_digit: string;
  error_password_missing_special: string;
  error_password_policy: string;
};

const WELCOME_TRANSLATIONS: Record<SupportedLocale, WelcomeKeys> = {
  de: {
    request_heading: 'Willkommen bei update Fitness',
    request_description:
      'Gib deine Daten und deinen Check-In Code ein. Wir senden dir deinen Benutzernamen und einen Link per E-Mail.',
    firstname_label: 'Vorname',
    lastname_label: 'Nachname',
    birthdate_label: 'Geburtsdatum',
    checkin_label: 'Check-In Code',
    request_submit: 'Link anfordern',
    request_sent_heading: 'E-Mail gesendet',
    request_sent_message:
      'Falls ein Konto mit diesen Daten existiert, haben wir dir eine E-Mail geschickt. Bitte prüfe deinen Posteingang.',
    error_required_fields: 'Bitte fülle alle Felder aus.',
    error_unexpected: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
    set_heading: 'Passwort festlegen',
    set_description: 'Lege ein Passwort für dein Konto fest.',
    reset_heading: 'Passwort zurücksetzen',
    reset_description: 'Lege ein neues Passwort für dein Konto fest.',
    reset_success_heading: 'Passwort zurückgesetzt',
    reset_success_message:
      'Dein Passwort wurde geändert. Du kannst dich jetzt mit deinem neuen Passwort anmelden.',
    username_label: 'Benutzername',
    password_label: 'Neues Passwort',
    password_repeat_label: 'Passwort bestätigen',
    set_submit: 'Passwort speichern',
    submitting: 'Wird gespeichert…',
    verifying: 'Link wird geprüft…',
    set_success_heading: 'Passwort gesetzt',
    set_success_message:
      'Dein Passwort wurde gespeichert. Du kannst dich jetzt mit deinem Benutzernamen anmelden.',
    link_invalid_heading: 'Link ungültig',
    link_invalid_message: 'Dieser Link ist ungültig oder abgelaufen. Bitte fordere einen neuen an.',
    request_new_link: 'Neuen Link anfordern',
    open_in_app: 'In der update Fitness App öffnen',
    password_hint:
      'Das Passwort muss 12 Zeichen lang sein und mindestens einen Grossbuchstaben, eine Ziffer und ein Sonderzeichen wie ! oder # enthalten und darf keinem Wort oder Wortteil entsprechen. Auch Teile des Benutzernamens werden nicht akzeptiert.',
    error_password_mismatch: 'Die Passwörter stimmen nicht überein.',
    error_password_too_short: 'Das Passwort muss mindestens 12 Zeichen lang sein.',
    error_password_missing_uppercase:
      'Das Passwort muss mindestens einen Grossbuchstaben enthalten.',
    error_password_missing_lowercase:
      'Das Passwort muss mindestens einen Kleinbuchstaben enthalten.',
    error_password_missing_digit: 'Das Passwort muss mindestens eine Ziffer enthalten.',
    error_password_missing_special:
      'Das Passwort muss mindestens ein Sonderzeichen wie ! oder # enthalten.',
    error_password_policy:
      'Das Passwort erfüllt nicht die Anforderungen. Bitte wähle ein stärkeres Passwort.',
  },
  en: {
    request_heading: 'Welcome to update Fitness',
    request_description:
      'Enter your details and check-in code. We will email you your username and a link.',
    firstname_label: 'First name',
    lastname_label: 'Last name',
    birthdate_label: 'Date of birth',
    checkin_label: 'Check-in code',
    request_submit: 'Request link',
    request_sent_heading: 'Email sent',
    request_sent_message:
      'If an account with these details exists, we have sent you an email. Please check your inbox.',
    error_required_fields: 'Please fill in all fields.',
    error_unexpected: 'Something went wrong. Please try again.',
    set_heading: 'Set your password',
    set_description: 'Choose a password for your account.',
    reset_heading: 'Reset your password',
    reset_description: 'Choose a new password for your account.',
    reset_success_heading: 'Password reset',
    reset_success_message:
      'Your password has been changed. You can now sign in with your new password.',
    username_label: 'Username',
    password_label: 'New password',
    password_repeat_label: 'Confirm password',
    set_submit: 'Save password',
    submitting: 'Saving…',
    verifying: 'Checking link…',
    set_success_heading: 'Password set',
    set_success_message: 'Your password has been saved. You can now sign in with your username.',
    link_invalid_heading: 'Invalid link',
    link_invalid_message: 'This link is invalid or expired. Please request a new one.',
    request_new_link: 'Request a new link',
    open_in_app: 'Open in the update Fitness app',
    password_hint:
      'The password must be 12 characters long and contain at least one upper-case letter, one digit and one special character such as ! or #. It must not match a word or part of a word, and parts of the username are not accepted either.',
    error_password_mismatch: 'The passwords do not match.',
    error_password_too_short: 'The password must be at least 12 characters long.',
    error_password_missing_uppercase: 'The password must contain at least one upper-case letter.',
    error_password_missing_lowercase: 'The password must contain at least one lower-case letter.',
    error_password_missing_digit: 'The password must contain at least one digit.',
    error_password_missing_special:
      'The password must contain at least one special character such as ! or #.',
    error_password_policy:
      'The password does not meet the requirements. Please choose a stronger password.',
  },
  fr: {
    request_heading: 'Bienvenue chez update Fitness',
    request_description:
      "Saisis tes données et ton code de check-in. Nous t'enverrons ton nom d'utilisateur et un lien par e-mail.",
    firstname_label: 'Prénom',
    lastname_label: 'Nom',
    birthdate_label: 'Date de naissance',
    checkin_label: 'Code de check-in',
    request_submit: 'Demander le lien',
    request_sent_heading: 'E-mail envoyé',
    request_sent_message:
      "Si un compte avec ces données existe, nous t'avons envoyé un e-mail. Vérifie ta boîte de réception.",
    error_required_fields: 'Merci de remplir tous les champs.',
    error_unexpected: 'Une erreur est survenue. Veuillez réessayer.',
    set_heading: 'Définir le mot de passe',
    set_description: 'Choisis un mot de passe pour ton compte.',
    reset_heading: 'Réinitialiser le mot de passe',
    reset_description: 'Choisis un nouveau mot de passe pour ton compte.',
    reset_success_heading: 'Mot de passe réinitialisé',
    reset_success_message:
      'Ton mot de passe a été modifié. Tu peux maintenant te connecter avec ton nouveau mot de passe.',
    username_label: "Nom d'utilisateur",
    password_label: 'Nouveau mot de passe',
    password_repeat_label: 'Confirmer le mot de passe',
    set_submit: 'Enregistrer le mot de passe',
    submitting: 'Enregistrement…',
    verifying: 'Vérification du lien…',
    set_success_heading: 'Mot de passe défini',
    set_success_message:
      "Ton mot de passe a été enregistré. Tu peux maintenant te connecter avec ton nom d'utilisateur.",
    link_invalid_heading: 'Lien invalide',
    link_invalid_message: 'Ce lien est invalide ou expiré. Demande un nouveau lien.',
    request_new_link: 'Demander un nouveau lien',
    open_in_app: "Ouvrir dans l'app update Fitness",
    password_hint:
      "Le mot de passe doit comporter 12 caractères et contenir au moins une majuscule, un chiffre et un caractère spécial comme ! ou #. Il ne doit correspondre à aucun mot ni fragment de mot, et les parties du nom d'utilisateur ne sont pas acceptées non plus.",
    error_password_mismatch: 'Les mots de passe ne correspondent pas.',
    error_password_too_short: 'Le mot de passe doit comporter au moins 12 caractères.',
    error_password_missing_uppercase:
      'Le mot de passe doit contenir au moins une lettre majuscule.',
    error_password_missing_lowercase:
      'Le mot de passe doit contenir au moins une lettre minuscule.',
    error_password_missing_digit: 'Le mot de passe doit contenir au moins un chiffre.',
    error_password_missing_special:
      'Le mot de passe doit contenir au moins un caractère spécial comme ! ou #.',
    error_password_policy:
      'Le mot de passe ne répond pas aux exigences. Choisis un mot de passe plus fort.',
  },
  it: {
    request_heading: 'Benvenuto in update Fitness',
    request_description:
      'Inserisci i tuoi dati e il codice di check-in. Ti invieremo il tuo nome utente e un link via e-mail.',
    firstname_label: 'Nome',
    lastname_label: 'Cognome',
    birthdate_label: 'Data di nascita',
    checkin_label: 'Codice check-in',
    request_submit: 'Richiedi link',
    request_sent_heading: 'E-mail inviata',
    request_sent_message:
      "Se esiste un account con questi dati, ti abbiamo inviato un'e-mail. Controlla la tua casella di posta.",
    error_required_fields: 'Compila tutti i campi.',
    error_unexpected: 'Qualcosa è andato storto. Riprova.',
    set_heading: 'Imposta la password',
    set_description: 'Scegli una password per il tuo account.',
    reset_heading: 'Reimposta la password',
    reset_description: 'Scegli una nuova password per il tuo account.',
    reset_success_heading: 'Password reimpostata',
    reset_success_message:
      'La tua password è stata modificata. Ora puoi accedere con la tua nuova password.',
    username_label: 'Nome utente',
    password_label: 'Nuova password',
    password_repeat_label: 'Conferma password',
    set_submit: 'Salva password',
    submitting: 'Salvataggio…',
    verifying: 'Verifica del link…',
    set_success_heading: 'Password impostata',
    set_success_message:
      'La tua password è stata salvata. Ora puoi accedere con il tuo nome utente.',
    link_invalid_heading: 'Link non valido',
    link_invalid_message: 'Questo link non è valido o è scaduto. Richiedi un nuovo link.',
    request_new_link: 'Richiedi un nuovo link',
    open_in_app: "Apri nell'app update Fitness",
    password_hint:
      'La password deve essere lunga 12 caratteri e contenere almeno una lettera maiuscola, una cifra e un carattere speciale come ! o #. Non deve corrispondere a una parola o a parte di essa e non sono accettate nemmeno parti del nome utente.',
    error_password_mismatch: 'Le password non corrispondono.',
    error_password_too_short: 'La password deve contenere almeno 12 caratteri.',
    error_password_missing_uppercase: 'La password deve contenere almeno una lettera maiuscola.',
    error_password_missing_lowercase: 'La password deve contenere almeno una lettera minuscola.',
    error_password_missing_digit: 'La password deve contenere almeno una cifra.',
    error_password_missing_special:
      'La password deve contenere almeno un carattere speciale come ! o #.',
    error_password_policy: 'La password non soddisfa i requisiti. Scegli una password più forte.',
  },
};

/**
 * Resolves the active locale, preferring an explicit `?lang=` query override
 * (so the kernel can match the email's language) and falling back to the
 * browser language, then 'de'.
 *
 * @param search - `window.location.search`.
 */
export function resolve_locale(search: string): SupportedLocale {
  const supported = SUPPORTED_LOCALES as readonly string[];
  const requested = (new URLSearchParams(search).get('lang') ?? '').slice(0, 2).toLowerCase();
  if (supported.includes(requested)) return requested as SupportedLocale;
  const nav =
    typeof navigator !== 'undefined' ? (navigator.language ?? '').slice(0, 2).toLowerCase() : '';
  if (supported.includes(nav)) return nav as SupportedLocale;
  return 'de';
}

/**
 * Returns the typed welcome translation table for a locale.
 *
 * @param locale - One of the supported locales.
 */
export function get_welcome_translations(locale: SupportedLocale): WelcomeKeys {
  return WELCOME_TRANSLATIONS[locale];
}
